import { createClient } from '@connectrpc/connect'
import { createConnectQueryKey, useMutation, useQuery } from '@connectrpc/connect-query'
import { useQueryClient, useMutation as useTanstackMutation } from '@tanstack/react-query'

import {
  getGetOngoingAssessmentQueryKey,
  getListOngoingAssessmentsQueryKey,
} from '@/api/assessments.hook'
import { BULK_MUTATION_CONCURRENCY, REFETCH_INTERVAL_MS } from '@/api/consts'
import { ControlDataQueryKey, ControlWithSignalsAndTimeline } from '@/api/export-company.hook'
import { getGetScopeQueryKey, getListScopesQueryKey } from '@/api/scope.hook'
import { getGetSignalQueryKey, getListSignalsQueryKey } from '@/api/signals.hook'
import { ControlStatus } from '@/gen/controls/v1/control_status_pb'
import {
  createControl,
  createOrUpdateOrgControlDefinition,
  deleteControl,
  getControl,
  getControlTimeline,
  listControlCategories,
  listControlSettings,
  listControls,
  markControlCommentsViewed,
  updateControl,
  updateControlSettings,
} from '@/gen/inventory/v1/control_service-ControlInventoryService_connectquery'
import {
  Control,
  ControlInventoryService,
  ControlSetting,
  ControlStatusChangeReason,
  GetControlResponse,
  ListControlsRequest,
  ListControlsResponse,
} from '@/gen/inventory/v1/control_service_pb'
import { GetSignalResponse, SignalService } from '@/gen/inventory/v1/signal_service_pb'

import { sessionTokenInterceptor } from '@/lib/auth/session-token-interceptor'
import { settleWithConcurrencyLimit } from '@/lib/collection/async-utils'
import { defaultTransport, getConnectTransport } from '@/lib/connect/connect-transport'

export type ControlWithSignals = GetControlResponse & {
  signals: GetSignalResponse[]
}

const controlInventoryClient = createClient(
  ControlInventoryService,
  getConnectTransport({
    interceptors: [sessionTokenInterceptor],
  }),
)

const signalServiceClient = createClient(
  SignalService,
  getConnectTransport({
    interceptors: [sessionTokenInterceptor],
  }),
)

// --- Query Key Factories ---

export const getListControlSettingsQueryKey = () =>
  createConnectQueryKey({
    schema: ControlInventoryService.method.listControlSettings,
    transport: defaultTransport,
    cardinality: 'finite',
  })

export const getGetControlQueryKey = (controlId?: string) =>
  createConnectQueryKey({
    schema: ControlInventoryService.method.getControl,
    input: { controlId },
    transport: defaultTransport,
    cardinality: 'finite',
  })

export const getGetControlTimelineQueryKey = (controlId: string) =>
  createConnectQueryKey({
    schema: ControlInventoryService.method.getControlTimeline,
    input: { controlId },
    transport: defaultTransport,
    cardinality: 'finite',
  })

// --- Read Hooks ---

export const useListControlCategories = () => {
  return useQuery(listControlCategories)
}

export const useUpdateControlSettings = () => {
  const queryClient = useQueryClient()

  return useMutation(updateControlSettings, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getListControlSettingsQueryKey(),
      })
      queryClient.invalidateQueries({ queryKey: getListControlsQueryKey() })
      queryClient.invalidateQueries({ queryKey: getGetControlQueryKey() })
      queryClient.invalidateQueries({ queryKey: getGetSignalQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListSignalsQueryKey() })
    },
  })
}

export const useDeleteControl = () => {
  const queryClient = useQueryClient()

  return useMutation(deleteControl, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListControlSettingsQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListControlsQueryKey() })
      queryClient.invalidateQueries({ queryKey: getGetControlQueryKey() })
    },
  })
}

export const useControlSettings = () => {
  return useQuery(listControlSettings, undefined, {
    select: (data) => {
      return {
        ...data,
        controlSettings: [...data.controlSettings].sort(
          (a, b) => a.orderPriority - b.orderPriority,
        ),
      }
    },
  })
}

export const getListControlsQueryKey = () =>
  createConnectQueryKey({
    schema: ControlInventoryService.method.listControls,
    transport: defaultTransport,
    cardinality: 'finite',
  })

const selectSortedControls = (data: ListControlsResponse): Control[] =>
  [...(data.controls ?? [])].sort((a, b) => a.orderPriority - b.orderPriority)

export const useListControls = ({
  enabled = true,
  ...request
}: ListControlsRequest & { enabled?: boolean }) => {
  const queryState = useQuery(listControls, request, {
    enabled,
    select: selectSortedControls,
    refetchInterval: REFETCH_INTERVAL_MS,
  })
  return {
    ...queryState,
    data: queryState.data ?? [],
  }
}

export const useGetControl = (controlId?: string, options?: { enabled?: boolean }) => {
  const queryState = useQuery(
    getControl,
    { controlId },
    {
      enabled: options?.enabled ?? !!controlId,
      refetchOnWindowFocus: false,
    },
  )

  return {
    ...queryState,
    data: queryState.data,
  }
}

export const useUpdateControl = (controlId: string) => {
  const queryClient = useQueryClient()

  return useMutation(updateControl, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getGetOngoingAssessmentQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListOngoingAssessmentsQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListControlsQueryKey() })
      queryClient.invalidateQueries({
        queryKey: getGetControlQueryKey(controlId),
      })
      queryClient.invalidateQueries({
        queryKey: getGetControlTimelineQueryKey(controlId),
      })
      queryClient.invalidateQueries({ queryKey: [ControlDataQueryKey] })
    },
  })
}

export const useMarkControlCommentsViewed = () => {
  const queryClient = useQueryClient()

  return useMutation(markControlCommentsViewed, {
    onSuccess: (_data, { controlId }) => {
      queryClient.invalidateQueries({ queryKey: getListControlsQueryKey() })
      queryClient.invalidateQueries({ queryKey: getGetControlQueryKey(controlId) })
    },
  })
}

export const useControlTimeline = (controlId: string) => {
  const queryState = useQuery(
    getControlTimeline,
    { controlId },
    {
      select: (data) => data.items ?? [],
      refetchInterval: 5000,
    },
  )
  return {
    ...queryState,
    data: queryState.data ?? [],
  }
}

export const useCreateOrUpdateOrgControlDefinition = () => {
  const queryClient = useQueryClient()

  return useMutation(createOrUpdateOrgControlDefinition, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getListControlSettingsQueryKey(),
      })
      queryClient.invalidateQueries({ queryKey: getListControlsQueryKey() })
      queryClient.invalidateQueries({ queryKey: getGetControlQueryKey() })
      queryClient.invalidateQueries({ queryKey: getGetSignalQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListSignalsQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListScopesQueryKey() })
      queryClient.removeQueries({ queryKey: getGetScopeQueryKey() })
    },
  })
}

export const useCreateControl = () => {
  const queryClient = useQueryClient()

  return useMutation(createControl, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListControlSettingsQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListControlsQueryKey() })
    },
  })
}

export const fetchControlsExtended = async (
  controlIds: string[],
): Promise<ControlWithSignals[]> => {
  if (controlIds.length === 0) {
    return []
  }

  const controlsResponses = await Promise.allSettled(
    controlIds.map((controlId) =>
      controlInventoryClient.getControl({
        controlId: controlId,
        staticOutput: true,
      }),
    ),
  )

  const successfulControls = controlsResponses
    .filter(
      (result): result is PromiseFulfilledResult<GetControlResponse> =>
        result.status === 'fulfilled',
    )
    .map((result) => result.value)

  const controlWithSignalResponses = await Promise.allSettled(
    successfulControls.map(async (control) => {
      const signalResponses = await Promise.allSettled(
        (control.signalIds || []).map((id) =>
          signalServiceClient.getSignal({
            id,
            staticOutput: true,
          }),
        ),
      )

      const successfulSignals = signalResponses
        .filter(
          (result): result is PromiseFulfilledResult<GetSignalResponse> =>
            result.status === 'fulfilled',
        )
        .map((result) => result.value)

      return {
        ...control,
        signals: successfulSignals,
      }
    }),
  )

  return controlWithSignalResponses
    .filter(
      (result): result is PromiseFulfilledResult<ControlWithSignalsAndTimeline> =>
        result.status === 'fulfilled',
    )
    .map((result) => result.value)
}

export const useBulkUpdateControlSettings = () => {
  const queryClient = useQueryClient()

  return useTanstackMutation({
    mutationFn: ({
      controls,
      patch,
    }: {
      controls: ControlSetting[]
      patch: {
        controlWeight?: number
        controlScopeId?: string
        enabled?: boolean
      }
    }) =>
      settleWithConcurrencyLimit(
        controls,
        (c) =>
          controlInventoryClient.updateControlSettings({
            controlId: c.controlId,
            controlScopeId: patch.controlScopeId ?? c.scope?.id,
            controlWeight: patch.controlWeight ?? c.controlWeight,
            enabled: patch.enabled ?? c.enabled,
            visible: c.visible,
          }),
        BULK_MUTATION_CONCURRENCY,
      ),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: getListControlSettingsQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListControlsQueryKey() })
      queryClient.invalidateQueries({ queryKey: getGetControlQueryKey() })
      queryClient.invalidateQueries({ queryKey: getGetSignalQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListSignalsQueryKey() })
    },
  })
}

export const useBulkUpdateControlStatus = () => {
  const queryClient = useQueryClient()

  return useTanstackMutation({
    mutationFn: ({
      controlIds,
      status,
      reason,
      comment,
    }: {
      controlIds: string[]
      status: ControlStatus
      reason: ControlStatusChangeReason
      comment?: string
    }) =>
      settleWithConcurrencyLimit(
        controlIds,
        (id) => controlInventoryClient.updateControl({ id, status, reason, comment }),
        BULK_MUTATION_CONCURRENCY,
      ),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: getGetOngoingAssessmentQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListOngoingAssessmentsQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListControlsQueryKey() })
      queryClient.invalidateQueries({ queryKey: [ControlDataQueryKey] })
    },
  })
}
